import React from 'react'
import { defineMessages, FormattedMessage } from 'react-intl'
import cx from 'classnames'
import { QUIZ_STATUS_FINISHED, QUIZ_STATUS_STARTED } from '@/constants'
import { Heading, Link } from '@/components'
import './DashboardPageQuizMastery.scss'

const DashboardPageQuizMasteryMessages = defineMessages({
  description: {
    defaultMessage:
      'You have not achieved 100% mastery of your Quizzes as indicated by the {quizIcon} icon.',
    description: 'Description for Quiz Mastery on the Dashboard page.',
    id: 'DashboardPageQuizMastery.description',
  },
  descriptionRecommended: {
    defaultMessage: 'You have not yet achieved mastery of your Quizzes!',
    description:
      'Description for Quiz Mastery when it is optional on the Dashboard page.',
    id: 'DashboardPageQuizMastery.descriptionRecommended',
  },
  descriptionDone: {
    defaultMessage:
      'Congratulations, you have achieved 100% mastery of your Quizzes!',
    description:
      'Description for Quiz Mastery when all Quizzes have been mastered on the Dashboard page.',
    id: 'DashboardPageQuizMastery.descriptionDone',
  },
  descriptionEmpty: {
    defaultMessage:
      'Check back as you complete Sections to ensure you have not missed a Quiz!',
    description:
      'Description for Quiz Mastery when all unlocked Quizzes have been mastered on the Dashboard page.',
    id: 'DashboardPageQuizMastery.descriptionEmpty',
  },
  descriptionRequirements: {
    defaultMessage:
      'You must take or reattempt the following {numSections} {numSections, plural, one {Quiz} other {Quizzes}} before you complete this course.',
    description:
      'Description for Quiz Mastery requirements on the Dashboard page.',
    id: 'DashboardPageQuizMastery.descriptionRequirements',
  },
  descriptionRequirementsRecommended: {
    defaultMessage:
      'It is recommended to attempt and pass the following {numSections} optional {numSections, plural, one {Quiz} other {Quizzes}}.',
    description:
      'Description for Quiz Mastery requirements when it is optional on the Dashboard page.',
    id: 'DashboardPageQuizMastery.descriptionRequirementsRecommended',
  },
  heading: {
    defaultMessage: 'Quiz Mastery (Required)',
    description: 'Title for Quiz Mastery on the Dashboard page.',
    id: 'DashboardPageQuizMastery.heading',
  },
  headingRecommended: {
    defaultMessage: 'Quiz Mastery (Recommended)',
    description:
      'Title for Quiz Mastery when it is optional on the Dashboard page.',
    id: 'DashboardPageQuizMastery.headingRecommended',
  },
  isDone: {
    defaultMessage: "You've completed all of the Quizzes!",
    description:
      'Messaing for Quiz Mastery when all Sections with Quiz have been mastered on the Dashboard page.',
    id: 'DashboardPageQuizMastery.isDone',
  },
  isEmpty: {
    defaultMessage: 'You have mastered all Quizzes you have unlocked so far!',
    description:
      'Messaing for Quiz Mastery when all available Sections with Quizzes have been mastered on the Dashboard page.',
    id: 'DashboardPageQuizMastery.isEmpty',
  },
  labelDefault: {
    defaultMessage: 'Begin Quiz',
    description:
      'CTA for Quiz Mastery link for a Section where Quiz has not been attempted on the Dashboard page.',
    id: 'DashboardPageQuizMastery.labelDefault',
  },
  labelStarted: {
    defaultMessage: 'Continue Quiz',
    description:
      'CTA for Quiz Mastery link for a Section where last Quiz is started on the Dashboard page.',
    id: 'DashboardPageQuizMastery.labelStarted',
  },
  labelFinished: {
    defaultMessage: 'Try Again',
    description:
      'CTA for Quiz Mastery link for a Section where last Quiz is finished on the Dashboard page.',
    id: 'DashboardPageQuizMastery.labelFinished',
  },
})

const __getCta = (quizStatus) => {
  if (quizStatus === QUIZ_STATUS_STARTED) {
    return DashboardPageQuizMasteryMessages.labelStarted
  } else if (quizStatus === QUIZ_STATUS_FINISHED) {
    return DashboardPageQuizMasteryMessages.labelFinished
  }
  return DashboardPageQuizMasteryMessages.labelDefault
}

const __getDescriptionRequirements = (isQuizMastery) => {
  if (isQuizMastery) {
    return DashboardPageQuizMasteryMessages.descriptionRequirements
  }
  return DashboardPageQuizMasteryMessages.descriptionRequirementsRecommended
}

const __getDescription = (isQuizMastery) => {
  if (isQuizMastery) return DashboardPageQuizMasteryMessages.description
  return DashboardPageQuizMasteryMessages.descriptionRecommended
}

const DashboardPageQuizMastery = ({
  intl,
  isDone,
  isQuizMastery,
  sections,
}) => {
  const { formatMessage } = intl
  return (
    <div className="DashboardPageQuizMastery">
      <Heading className="DashboardPageQuizMastery__title" is3>
        {isQuizMastery
          ? formatMessage(DashboardPageQuizMasteryMessages.heading)
          : formatMessage(DashboardPageQuizMasteryMessages.headingRecommended)}
      </Heading>
      <div className="DashboardPageQuizMastery__main">
        <div className="DashboardPageQuizMastery__description">
          {!isDone && (
            <p>
              {
                // is not empty
                sections.length ? (
                  <FormattedMessage
                    {...__getDescription(isQuizMastery)}
                    values={{
                      quizIcon: (
                        <i
                          aria-hidden="true"
                          className="fa fa-question-circle DashboardPageQuizMastery__description-icon"
                        />
                      ),
                    }}
                  />
                ) : (
                  // is empty
                  formatMessage(
                    DashboardPageQuizMasteryMessages.descriptionEmpty,
                  )
                )
              }
              &nbsp;
              {
                // is done
                isDone &&
                  formatMessage(
                    DashboardPageQuizMasteryMessages.descriptionDone,
                  )
              }
              {
                // is not empty
                !isDone &&
                  !!sections.length &&
                  formatMessage(__getDescriptionRequirements(isQuizMastery), {
                    numSections: sections.length,
                  })
              }
            </p>
          )}
        </div>
        <div className="DashboardPageQuizMastery__sections">
          {!sections.length && (
            <p
              className={cx('DashboardPageQuizMastery__sections-messaging', {
                'DashboardPageQuizMastery__sections-messaging--is-done': isDone,
                'DashboardPageQuizMastery__sections-messaging--is-empty':
                  !isDone,
              })}
            >
              <span className="DashboardPageQuizMastery__sections-messaging-text">
                {isDone
                  ? formatMessage(DashboardPageQuizMasteryMessages.isDone)
                  : formatMessage(DashboardPageQuizMasteryMessages.isEmpty)}
              </span>
            </p>
          )}
          {!isDone && !!sections.length && (
            <ul className="DashboardPageQuizMastery__sections-list">
              {sections.map(
                (
                  {
                    contentfulId: sectionContentfulId,
                    quizStatus: sectionQuizStatus,
                    title: sectionTitle,
                    unitContentfulId: sectionUnitContentfulId,
                  },
                  index,
                ) => (
                  <li
                    className="DashboardPageQuizMastery__sections-list-item"
                    key={`DashboardPageQuizMastery__sections-list-item-${index}`}
                  >
                    <Link
                      className="DashboardPageQuizMastery__sections-list-item-link"
                      to={`/units/${sectionUnitContentfulId}/sections/${sectionContentfulId}/quiz`}
                    >
                      <Heading
                        className="DashboardPageQuizMastery__sections-list-item-heading"
                        is5
                      >
                        {sectionTitle}
                      </Heading>
                      <div className="DashboardPageQuizMastery__sections-list-item-cta">
                        <span className="DashboardPageQuizMastery__sections-list-item-cta-label">
                          {formatMessage(__getCta(sectionQuizStatus))}
                        </span>
                        <i
                          aria-hidden="true"
                          className="DashboardPageQuizMastery__sections-list-item-cta-icon fa fa-angle-right"
                        />
                      </div>
                    </Link>
                  </li>
                ),
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default DashboardPageQuizMastery
